import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useEmbedStore } from '@/stores/embed-store';
import { billingApi } from '@/lib/api/billing';
import { PlanType, SubscriptionStatus } from '@optidev/shared-types';

/**
 * In embed mode, checks if the OptiDev add-on subscription is active.
 * Returns { isBlocked, loading } — isBlocked is true when embed subscription
 * is cancelled or doesn't exist.
 *
 * Re-checks on mount and when the page becomes visible (user switches back).
 * For non-embed mode, always returns isBlocked: false.
 */
export function useEmbedSubscriptionGate(): { isBlocked: boolean; loading: boolean } {
  const { isEmbedded, embedSource } = useEmbedStore();
  const needsAdminAccess = useEmbedStore((s) => s.needsAdminAccess);
  const needsProvisioning = useEmbedStore((s) => s.needsProvisioning);
  const { getIdToken } = useAuth();
  const [isBlocked, setIsBlocked] = useState(false);
  const [loading, setLoading] = useState(true);

  const checkSubscription = useCallback(async () => {
    if (!isEmbedded || embedSource !== 'optisigns') {
      setIsBlocked(false);
      setLoading(false);
      return;
    }

    // Skip the subscription check whenever the user doesn't yet have an OptiDev
    // backend account. Two cases:
    //  - needsAdminAccess: non-admin OptiSigns user; never had an account.
    //  - needsProvisioning: admin/owner pre-provision; the OptiDev account is
    //    created lazily when they click "Build it" (PROVISION_WORKSPACE →
    //    /api/workspaces/provision). Before that, AuthContext is unpopulated
    //    and getIdToken() returns null — which the legacy "no token = blocked"
    //    branch below would misread as a cancelled subscription, surfacing
    //    "OptiDev add-on inactive" instead of EmbedOnboarding.
    // The cancelled-sub flow for real subscribers (logged-in admins with a
    // workspace) is unaffected — they don't hit this branch.
    if (needsAdminAccess || needsProvisioning) {
      setIsBlocked(false);
      setLoading(false);
      return;
    }

    try {
      const token = await getIdToken();
      if (!token) {
        setIsBlocked(true);
        return;
      }
      const sub = await billingApi.getSubscription(token);
      // Only block when embed subscription exists but is cancelled
      // Don't block when there's no embed subscription (user hasn't started trial yet)
      const isCancelledEmbed = sub.planType === PlanType.EMBED
        && sub.status === SubscriptionStatus.CANCELLED;
      setIsBlocked(isCancelledEmbed);
    } catch {
      setIsBlocked(false);
    } finally {
      setLoading(false);
    }
  }, [isEmbedded, embedSource, needsAdminAccess, needsProvisioning, getIdToken]);

  // Check on mount
  useEffect(() => {
    checkSubscription();
  }, [checkSubscription]);

  // Re-check when page becomes visible (user comes back from OptiSigns billing)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        checkSubscription();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [checkSubscription]);

  return { isBlocked, loading };
}
